/**
 * Bruku Currency Selector — cosmetic, client-side-only currency conversion.
 *
 * IMPORTANT: this only changes how prices are DISPLAYED in the storefront.
 * The shop's real currency (and what the customer is actually charged at
 * checkout) is always COP. Nothing here touches Shopify's price data.
 *
 * How it works:
 * 1. Every rendered money string in the DOM (e.g. "$40.000 COP", "$40.000")
 *    gets wrapped in a <span class="bruku-money" data-cop="40000"> the first
 *    time it's seen, so the ORIGINAL peso amount is always available even
 *    after switching currencies back and forth (no re-parsing of already
 *    converted text, so there's no rounding drift).
 * 2. A MutationObserver re-scans any new DOM the theme injects later
 *    (cart drawer, ajax cart updates, quick add, predictive search,
 *    variant price swaps, etc.) so newly added prices get wrapped too.
 * 3. Switching currency just re-renders every wrapped span from its
 *    original COP value — instant, no page reload.
 * 4. The chosen currency is remembered in localStorage.
 *
 * Adding a new currency later: add one entry to CURRENCIES below (and a
 * matching <li role="option"> in snippets/bruku-currency-selector.liquid).
 * No other code needs to change.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bruku:currency';
  var DEFAULT_CURRENCY = 'COP';

  // Fixed manual exchange rates relative to COP (the shop's real currency).
  // rate = how many units of this currency equal 1 COP.
  // USD rate updated 2026-08-25 based on TRM ~3061 COP/USD (was 3300).
  var CURRENCIES = {
    COP: { code: 'COP', symbol: '$', spaceAfterSymbol: false, rate: 1, decimals: 0 },
    PEN: { code: 'PEN', symbol: 'S/', spaceAfterSymbol: true, rate: 0.001, decimals: 0 },
    MXN: { code: 'MXN', symbol: '$', spaceAfterSymbol: false, rate: 0.005, decimals: 0 },
    USD: { code: 'USD', symbol: '$', spaceAfterSymbol: false, rate: 0.00032672, decimals: 0 }
  };

  // Matches Shopify-rendered peso amounts: "$40.000", "$40.000,00", "$40.000 COP"...
  var MONEY_REGEX = /\$\s?\d{1,3}(?:\.\d{3})*(?:,\d{2})?(?:\s?COP)?/g;

  function getCurrency() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (stored && CURRENCIES[stored]) return stored;
    } catch (e) {}
    return DEFAULT_CURRENCY;
  }

  function setCurrency(key) {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch (e) {}
  }

  function formatNumber(n) {
    return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function formatFull(cop, key) {
    var cur = CURRENCIES[key] || CURRENCIES[DEFAULT_CURRENCY];
    var sep = cur.spaceAfterSymbol ? ' ' : '';
    return cur.symbol + sep + formatNumber(cop * cur.rate) + ' ' + cur.code;
  }

  function formatAmountOnly(cop, key) {
    var cur = CURRENCIES[key] || CURRENCIES[DEFAULT_CURRENCY];
    var sep = cur.spaceAfterSymbol ? ' ' : '';
    return cur.symbol + sep + formatNumber(cop * cur.rate);
  }

  function parseCop(matchedText) {
    var s = matchedText.replace(/COP/i, '').replace(/\$/g, '').trim();
    if (s.indexOf(',') !== -1) s = s.split(',')[0];
    s = s.replace(/\./g, '');
    var n = parseInt(s, 10);
    return isNaN(n) ? null : n;
  }

  function shouldSkipParent(parent) {
    if (!parent || parent.nodeType !== 1) return true;
    var tag = parent.nodeName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA' || tag === 'TITLE') return true;
    if (parent.classList && parent.classList.contains('bruku-money')) return true;
    if (parent.closest && parent.closest('[data-bruku-currency]')) return true;
    return false;
  }

  function wrapTextNode(textNode) {
    var parent = textNode.parentNode;
    if (shouldSkipParent(parent)) return;

    var text = textNode.nodeValue;
    if (!text || text.indexOf('$') === -1) return;

    MONEY_REGEX.lastIndex = 0;
    var match;
    var lastIndex = 0;
    var frag = null;

    while ((match = MONEY_REGEX.exec(text)) !== null) {
      var cop = parseCop(match[0]);
      if (cop === null) continue;
      if (!frag) frag = document.createDocumentFragment();

      if (match.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      var span = document.createElement('span');
      span.className = 'bruku-money';
      span.setAttribute('data-cop', String(cop));
      span.textContent = match[0];
      frag.appendChild(span);
      lastIndex = MONEY_REGEX.lastIndex;
    }

    if (!frag) return;
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    parent.replaceChild(frag, textNode);
  }

  function collectTextNodes(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (shouldSkipParent(node.parentNode)) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue || node.nodeValue.indexOf('$') === -1) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }

  // Some custom sections (hero slideshow, combo cards) render the amount
  // and the currency code as two separate elements: <span class="amount">
  // + <span class="note">. Keep the "note" element (currently just static
  // "COP" text) in sync with whatever currency is selected.
  function tagNoteSiblings(root) {
    if (!root.querySelectorAll) return;
    var containers = root.querySelectorAll('.bruku-slide-price, .bruku-combo-price');
    containers.forEach(function (container) {
      var amount = container.querySelector('.bruku-money');
      var note = container.querySelector('.note');
      if (amount && note) {
        amount.setAttribute('data-bruku-amount-only', '1');
        note.setAttribute('data-bruku-currency-note', '1');
      }
    });
  }

  function scan(root) {
    collectTextNodes(root).forEach(wrapTextNode);
    tagNoteSiblings(root);
  }

  function applyCurrency(key) {
    document.querySelectorAll('.bruku-money').forEach(function (el) {
      var cop = parseInt(el.getAttribute('data-cop'), 10);
      if (isNaN(cop)) return;
      el.textContent = el.hasAttribute('data-bruku-amount-only')
        ? formatAmountOnly(cop, key)
        : formatFull(cop, key);
    });

    var cur = CURRENCIES[key] || CURRENCIES[DEFAULT_CURRENCY];
    document.querySelectorAll('[data-bruku-currency-note]').forEach(function (el) {
      el.textContent = cur.code;
    });
    document.querySelectorAll('[data-bruku-currency-code]').forEach(function (el) {
      el.textContent = cur.code;
    });
    document.querySelectorAll('.bruku-currency__menu [role="option"]').forEach(function (el) {
      var active = el.getAttribute('data-currency') === key;
      el.setAttribute('aria-selected', active ? 'true' : 'false');
      el.classList.toggle('is-active', active);
    });
  }

  var currentKey = getCurrency();
  var observer = null;

  function safeScanAndApply(root) {
    if (observer) observer.disconnect();
    scan(root);
    applyCurrency(currentKey);
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  function setupSelector(root) {
    var trigger = root.querySelector('.bruku-currency__trigger');
    var menu = root.querySelector('.bruku-currency__menu');
    if (!trigger || !menu || root.__brukuBound) return;
    root.__brukuBound = true;

    function close() {
      root.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    }
    function open() {
      root.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (root.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    menu.querySelectorAll('[role="option"]').forEach(function (option) {
      option.addEventListener('click', function () {
        var key = option.getAttribute('data-currency');
        if (!CURRENCIES[key] || key === currentKey) {
          close();
          return;
        }
        currentKey = key;
        setCurrency(key);
        applyCurrency(key);
        close();
        document.dispatchEvent(new CustomEvent('bruku:currencychange', { detail: { currency: key } }));
      });
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  function init() {
    observer = new MutationObserver(function (mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          relevant = true;
          break;
        }
      }
      if (!relevant) return;
      clearTimeout(window.__brukuCurrencyTimer);
      window.__brukuCurrencyTimer = setTimeout(function () {
        safeScanAndApply(document.body);
        document.querySelectorAll('[data-bruku-currency]').forEach(setupSelector);
      }, 40);
    });

    safeScanAndApply(document.body);
    document.querySelectorAll('[data-bruku-currency]').forEach(setupSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
